<template>
  <v-row class="ma-0">
    <v-col cols="12" order="1">
      <v-card flat outlined color="grey lighten-3">
        <v-card-text align="start">
          <v-row>
            <v-col class="pb-0">
              <div class="text-body-2">{{ $translate('STATUS') }}</div>
              <v-chip
                v-if="statusIcon[order.OrderGroupStatus] != null"
                class="mt-2 mb-3"
                :color="statusColor[order.OrderGroupStatus]"
              >
                <v-icon left color="white">
                  {{ statusIcon[order.OrderGroupStatus] }}
                </v-icon>
                <span class="chip-font-size white--text">
                  {{ $translate(statusMsg[order.OrderGroupStatus]) }}</span
                >
              </v-chip>
              <v-chip v-else class="mt-2 mb-3" color="grey lighten-1">
                <v-icon left color="white">info</v-icon>
                <span class="chip-font-size white--text">
                  {{ order.OrderGroupStatus }}
                </span>
              </v-chip>
            </v-col>

            <v-col class="pb-0">
              <div class="text-body-2">{{ $translate('ORDERID') }}</div>
              <h3 class="mb-3 text--primary">{{ order.OrderGroupId }}</h3>
            </v-col>

            <v-col class="pb-0">
              <div class="text-body-2">
                {{ $translate('REGISTER_FORM_EMAIL') }}
              </div>
              <h3 class="mb-3 text--primary">
                {{ order.Email }}
              </h3>
            </v-col>

            <v-col cols="12" class="pb-0">
              <v-row>
                <v-col v-if="isProfUser">
                  <div>{{ $translate('VAT') }}</div>
                  <h3 class="mb-3 text--primary">
                    {{ formatter.format(totalVatPrice) }}
                  </h3>
                </v-col>

                <v-col v-if="totalDiscount < -0.01">
                  <div>{{ $translate('DISCOUNT') }}</div>
                  <h3 class="mb-3 red--text">
                    {{ formatter.format(totalDiscount) }}
                  </h3>
                </v-col>

                <v-col>
                  <div class="text-body-2">
                    {{ $translate('LINDA_TOTAL_PRICE') }}
                  </div>
                  <h2 class="mb-3 text--primary">
                    {{ formatter.format(order.TotalPrice) }}
                  </h2>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-row
            v-if="
              order.OrderGroupStatus === 'RequestedPayment' ||
              order.OrderGroupStatus === 'WaitingForPayment'
            "
            class="ma-0"
          >
            <v-btn
              :loading="cancelOrderGroupProcessing"
              color="secondary"
              class="text-capitalize"
              @click="cancelOrderGroup(order.OrderGroupId)"
            >
              {{ $translate('CANCEL') }}
            </v-btn>
            <v-btn
              :loading="loading"
              color="primary"
              class="text-capitalize ml-3 primary-font-color"
              @click="confirmOrderGroup(order.OrderGroupId, order.Email)"
            >
              {{ $translate('PAY') }}
            </v-btn>
          </v-row>
          <v-btn
            :disabled="order.Invoice === ''"
            color="primary"
            v-else
            class="text-capitalize primary-font-color"
            @click="getOrderBinaryData(order)"
          >
            <v-icon left>mdi-download</v-icon>
            {{ $translate('ORDER_DOWNLOAD') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>

    <v-col cols="12" order="2">
      <v-card elevation="0">
        <v-card-title class="px-0 pt-0 pb-2">
          {{ $translate('ORDER_OVERVIEW') }}
        </v-card-title>
        <div
          v-for="(currentOrder, orderIndex) in order.Orders"
          :key="`order-${orderIndex}`"
          class="mb-4"
        >
          <v-card-text class="pa-0">
            <h4>{{ currentOrder.Name }}</h4>
            <p v-if="currentOrder.Type === 'Rental'" class="mb-3">
              {{ currentOrder.StartDate }}
              {{ currentOrder.StartTimeFE }}
              -
              {{ currentOrder.EndDate }}
              {{ currentOrder.EndTimeFE }}
            </p>
            <p v-else class="mb-3">
              {{ currentOrder.StartDate }}
              {{ currentOrder.StartTimeFE }}
            </p>

            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">
                      {{ $translate('NAME') }}
                    </th>
                    <th class="text-left">
                      {{ $translate('QUANTITY') }}
                    </th>
                    <th class="text-right">
                      {{ $translate('LINDA_TOTAL_PRICE') }}
                      <span v-if="isProfUser"
                        >({{ $translate('EXCL_VAT') }})</span
                      >
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <!-- Products  -->
                  <template v-if="currentOrder.Products.length">
                    <tr
                      v-for="(item, index) in currentOrder.Products"
                      :key="`order-${orderIndex}-product-${index}`"
                    >
                      <!-- Name -->
                      <td>
                        <v-col class="pa-0">
                          <div>{{ item.ProductName }}</div>
                          <div
                            v-if="item.Aspects !== ''"
                            class="text-caption text--secondary"
                          >
                            {{ item.Aspects }}
                          </div>
                          <div
                            v-for="(optn, index) in item.Options"
                            :key="index"
                          >
                            <ActivityOptionsView :item="optn" />
                          </div>
                        </v-col>
                      </td>
                      <!-- Quantity -->
                      <td>
                        {{ item.Quantity }}
                        <span
                          v-if="isWeightedProduct(item)"
                          class="text-caption text--secondary"
                          >{{ item.Unit }}</span
                        >
                      </td>
                      <!-- Total price -->
                      <td class="text-right">
                        <span
                          v-if="showProductRetailPrice(item)"
                          class="discount-price text-caption text--secondary mr-1"
                        >
                          {{
                            formatter.format(getProductTotalRetailPrice(item))
                          }}
                        </span>
                        {{ formatter.format(getProductTotalPrice(item)) }}
                      </td>
                    </tr>
                  </template>

                  <!-- Price categories -->
                  <template v-if="currentOrder.PriceCategories.length">
                    <tr
                      v-for="(item, index) in currentOrder.PriceCategories"
                      :key="`order-${orderIndex}-category-${index}`"
                    >
                      <td>{{ item.PriceCategoryName }}</td>
                      <td>{{ item.Quantity }}</td>
                      <td class="text-right">
                        {{ formatter.format(getPriceCategoryTotalPrice(item)) }}
                      </td>
                    </tr>
                  </template>

                  <!-- Special requests -->
                  <template v-if="currentOrder.SpecialRequests.length">
                    <tr
                      v-for="(item, index) in currentOrder.SpecialRequests"
                      :key="`order-${orderIndex}-request-${index}`"
                    >
                      <td>
                        ({{ $translate('SPECIAL_REQUEST') }})
                        {{ item.SpecialRequest }}
                      </td>
                      <td></td>
                      <td class="text-right">
                        {{ formatter.format(item.Price) }}
                      </td>
                    </tr>
                  </template>

                  <!-- Discounts -->
                  <template v-if="currentOrder.Discounts.length">
                    <tr
                      v-for="(item, index) in currentOrder.Discounts"
                      :key="`order-${orderIndex}-discount-${index}`"
                    >
                      <td>{{ item.Name }}</td>
                      <td></td>
                      <td class="red--text text-right">
                        {{ formatter.format(getDiscountPrice(item)) }}
                      </td>
                    </tr>
                  </template>

                  <!-- Delivery costs -->
                  <tr
                    v-for="(item, index) in currentOrder.DeliveryCosts"
                    :key="`order-${orderIndex}-delivery-${index}`"
                  >
                    <td>{{ getDeliveryCostTitle(currentOrder) }}</td>
                    <td></td>
                    <td class="text-right">
                      {{ formatter.format(getDeliveryCost(item)) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import doRequest from '@/api/dssRequest';
import { isWeightedProduct } from '@/objects/Product';
import { getTotalDiscount } from '@/objects/Order';
import ActivityOptionsView from './ActivityOptionsView';

export default {
  name: 'ActivityOrder',
  components: {
    ActivityOptionsView,
  },
  data() {
    return {
      loading: false,
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      statusColor: {
        Confirmed: 'success',
        PartnerConfirmation: 'warning',
        RequestedPayment: 'red',
        WaitingForPayment: 'red',
        Cancelled: 'red',
      },
      statusIcon: {
        Confirmed: 'mdi-check-circle',
        PartnerConfirmation: 'mdi-hand',
        RequestedPayment: 'mdi-alert',
        WaitingForPayment: 'mdi-alert',
        Cancelled: 'mdi-close-circle-outline',
      },
      statusMsg: {
        Confirmed: 'STATUS_CONFIRMED',
        PartnerConfirmation: 'STATUS_PARTNER_CONFIRM',
        RequestedPayment: 'STATUS_REQUESTED_PAYMENT',
        WaitingForPayment: 'STATUS_REQUESTED_PAYMENT',
        Cancelled: 'CANCELED',
      },
      reorderedProducts: {
        failed: [],
        succeeded: [],
      },
      cancelOrderGroupProcessing: false,
    };
  },
  props: ['order'],
  computed: {
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
    totalVatPrice() {
      let price = 0;
      if (this.order != null && this.order.Orders != null) {
        this.order.Orders.forEach((order) => {
          price += order.OrderTotalPriceVat;
        });
      }
      return price;
    },
    totalDiscount() {
      let price = 0;
      if (this.order != null && this.order.Orders != null) {
        this.order.Orders.forEach((order) => {
          price += getTotalDiscount(order);
        });
      }
      return price;
    },
  },
  methods: {
    isWeightedProduct,
    getOrderBinaryData(item) {
      const payload = {
        Id: item.Orders[0].OrderGroupId,
      };
      doRequest(
        (response) => {
          if (response.data.Data != null) {
            const source = `data:application/pdf;base64,${response.data.Data}`;
            const downloadLink = document.createElement('a');
            const fileName = 'order_' + item.OrderGroupId + '.pdf';
            downloadLink.href = source;
            downloadLink.download = fileName;
            downloadLink.click();

            const pdfWindow = window.open('');
            setTimeout(() => {
              pdfWindow.document.write(
                `<iframe width='100%' name='text' height='100%' src='data:application/pdf;base64, ${encodeURI(
                  response.data.Data
                )}'></iframe>`
              );
              pdfWindow.document.title = fileName;
            }, 0);
          }
        },
        (error) => {
        },
        'getOrderBinaryData',
        'LocationsNode_OSE',
        payload
      );
    },
    confirmOrderGroup(id, email) {
      this.loading = true;
      const payload = {
        OrderGroupId: id,
        Email: email,
      };
      doRequest(
        (response) => {
          setTimeout(() => {
            this.show = false;
            this.loading = false;
            window.location = response.data.PaymentUrl;
          }, 5000);
        },
        (error) => {
          setTimeout(() => {
            this.loading = false;
          }, 100);
          this.alertType = 'error';
          this.alertText = this.$translate('ALERT_TEXT_ERROR');
          setTimeout(() => {
            this.show = false;
          }, 1000);
        },
        'confirmOrderGroup',
        this.$store.getters['auth/userName'],
        payload,
        'POST',
        600000,
        false
      );
    },
    reorderGroup(id) {
      const payload = {
        OrderGroupId: id,
        Reservations: {},
      };
      const orderstate = this.$store.getters['shoppingCart/orders'];

      if (typeof orderstate[0] !== 'undefined') {
        payload.Reservations[orderstate[0].EventId] = [orderstate[0].OrderId];
      }

      doRequest(
        (response) => {
          for (let i in response.data.UpdatedReservations) {
            if (
              response.data.UpdatedReservations[i].MainOrder.Type !==
                'Delivery' ||
              response.data.UpdatedReservations[i].MainOrder.Type !==
                'Shiplee' ||
              response.data.UpdatedReservations[i].MainOrder.Type !== 'PickUp'
            ) {
              const order = {
                Date: new Date(),
                EventId: response.data.UpdatedReservations[i].EventInfo.EventId,
                OrderId: response.data.UpdatedReservations[i].MainOrder.OrderId,
                OrderTotalPrice:
                  response.data.UpdatedReservations[i].MainOrder
                    .OrderTotalPrice,
                PriceCategories: {},
                Products:
                  response.data.UpdatedReservations[i].MainOrder.Products,
                ShoppingCartType: 'Product',
              };

              this.$store.commit(
                'shoppingCart/addOrderWithoutReservation',
                order
              );
            }
          }
        },
        (error) => {
          this.alertType = 'error';
          this.alertText = this.$translate('ALERT_TEXT_ERROR');
          setTimeout(() => {
            this.show = false;
          }, 1000);
        },
        'addPreviousOrderGroupToReservations',
        this.$store.getters['auth/userName'],
        payload,
        'POST',
        600000,
        false
      );
    },
    cancelOrderGroup(orderGroupId) {
      const payload = { OrderGroupId: orderGroupId };
      this.cancelOrderGroupProcessing = true;

      doRequest(
        () => {
          this.cancelOrderGroupProcessing = false;
          this.$emit('reload');
          window.EventBus.fire(
            'activateSnackBar',
            this.$translate('CANCEL_ORDER_GROUP_SUCCESS')
          );
        },
        () => {
          this.cancelOrderGroupProcessing = false;
          window.EventBus.fire(
            'activateSnackBar',
            this.$translate('CANCEL_ORDER_GROUP_ERROR')
          );
        },
        'cancelOrderGroup',
        this.$store.getters['auth/userName'],
        payload
      );
    },
    getProductTotalPrice(orderProduct) {
      if (this.isProfUser) {
        return orderProduct.ItemTotalPrice - orderProduct.ItemTotalVatPrice;
      }
      return orderProduct.ItemTotalPrice;
    },
    getProductTotalRetailPrice(orderProduct) {
      let amount = orderProduct.Quantity;
      if (isWeightedProduct(orderProduct)) {
        amount = orderProduct.Quantity / orderProduct.StepSize;
      }

      if (this.isProfUser) {
        return (
          amount *
          (orderProduct.ItemRetailPrice - orderProduct.ItemRetailVatPrice)
        );
      }
      return amount * orderProduct.ItemRetailPrice;
    },
    showProductRetailPrice(orderProduct) {
      const retailPrice = Number(
        this.getProductTotalRetailPrice(orderProduct)
      ).toFixed(2);
      const price = Number(this.getProductTotalPrice(orderProduct)).toFixed(2);

      return retailPrice > 0 && retailPrice > price;
    },
    getPriceCategoryTotalPrice(priceCategory) {
      if (this.isProfUser) {
        return (
          priceCategory.Quantity *
          (priceCategory.PriceCategoryPrice -
            priceCategory.PriceCategoryVatPrice)
        );
      }
      return priceCategory.Quantity * priceCategory.PriceCategoryPrice;
    },
    getDiscountPrice(discount) {
      if (this.isProfUser) {
        return discount.Price - discount.VatPrice;
      }
      return discount.Price;
    },
    getDeliveryCost(delivery) {
      if (this.isProfUser) {
        return delivery.DeliveryPrice - delivery.DeliveryVatPrice;
      }
      return delivery.DeliveryPrice;
    },
    getDeliveryCostTitle(deliveryPickupOrder) {
      if (deliveryPickupOrder.Type === 'PickUp') {
        return this.$translate('PICKUP_FEE');
      }
      return this.$translate('DELIVERY_FEE');
    },
  },
};
</script>
